import { ListingCardProps } from '../components/ListingCard'

/**
 * Furniture is decided on three things a photograph can't answer: will it fit,
 * what is it made of, and when will it actually arrive. So dimensions, finish
 * and lead time are the card, not the detail page.
 */
export interface FurnitureFinish {
  id: string
  label: string
  /** Swatch colour — fabric or timber. */
  hex: string
  image: string
  /** Some finishes are made to order rather than held in stock. */
  leadTime?: string
}

export interface Furniture {
  brand: string
  /** "3-seater sofa", "Dining table", "Armchair" */
  type: string
  material: string
  /** Width × depth × height, in cm. */
  dimensions: {
    w: number
    d: number
    h: number
  }
  finishes: FurnitureFinish[]
  price: number
  wasPrice?: number
  /** Default availability when the chosen finish doesn't override it. */
  leadTime: string
  inStock: boolean
  assembly: string
  /** Free over a threshold, or a flat charge. */
  delivery: string
  care?: string
  ordering?: boolean
}

const SOFA_SAND =
  'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=900'
const SOFA_GREY =
  'https://images.unsplash.com/photo-1493666438817-866a91353ca9?auto=format&fit=crop&q=80&w=900'
const DINING =
  'https://images.unsplash.com/photo-1617806118233-18e1de247200?auto=format&fit=crop&q=80&w=900'
const DINING_2 =
  'https://images.unsplash.com/photo-1615875605825-5eb9bb5d52ac?auto=format&fit=crop&q=80&w=900'
const ARMCHAIR =
  'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?auto=format&fit=crop&q=80&w=900'
const ARMCHAIR_2 =
  'https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?auto=format&fit=crop&q=80&w=900'
const BED =
  'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&q=80&w=900'
const BED_2 =
  'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?auto=format&fit=crop&q=80&w=900'
const DESK =
  'https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&q=80&w=900'
const SHELF =
  'https://images.unsplash.com/photo-1594026112284-02bb6f3352fe?auto=format&fit=crop&q=80&w=900'

export const furnitureListings: (ListingCardProps & {
  furniture: Furniture
})[] = [
  {
    id: 'f-1',
    title: 'Lumen 3-Seater Sofa',
    price: 'AED 4,900',
    location: 'Atelier Maison · Al Quoz',
    images: [SOFA_SAND, SOFA_GREY],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    furniture: {
      brand: 'Atelier Maison',
      type: '3-seater sofa',
      material: 'Boucle upholstery · Solid oak legs',
      dimensions: {
        w: 220,
        d: 95,
        h: 78,
      },
      price: 4900,
      wasPrice: 5900,
      leadTime: 'In stock · delivered in 5 days',
      inStock: true,
      assembly: 'Assembled on delivery, free',
      delivery: 'Free delivery across the UAE',
      care: 'Removable covers, dry clean only',
      finishes: [
        {
          id: 'sand',
          label: 'Sand boucle',
          hex: '#D9CBB5',
          image: SOFA_SAND,
        },
        {
          id: 'mist',
          label: 'Mist grey',
          hex: '#A9AFB3',
          image: SOFA_GREY,
          leadTime: 'Made to order · 3 weeks',
        },
      ],
    },
  },
  {
    id: 'f-2',
    title: 'Kade Oak Dining Table',
    price: 'AED 3,450',
    location: 'Atelier Maison · Al Quoz',
    images: [DINING, DINING_2],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    furniture: {
      brand: 'Atelier Maison',
      type: 'Dining table · seats 6',
      material: 'Solid European oak · Matte lacquer',
      dimensions: {
        w: 200,
        d: 100,
        h: 75,
      },
      price: 3450,
      leadTime: 'In stock · delivered in 5 days',
      inStock: true,
      assembly: 'Legs fitted on delivery, free',
      delivery: 'Free delivery across the UAE',
      care: 'Wipe with a damp cloth, re-oil yearly',
      finishes: [
        {
          id: 'natural',
          label: 'Natural oak',
          hex: '#C8A579',
          image: DINING,
        },
        {
          id: 'smoked',
          label: 'Smoked oak',
          hex: '#6E5741',
          image: DINING_2,
        },
      ],
    },
  },
  {
    id: 'f-3',
    title: 'Orla Lounge Chair',
    price: 'AED 1,780',
    location: 'Nordic Rooms · Dubai Design District',
    images: [ARMCHAIR, ARMCHAIR_2],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    furniture: {
      brand: 'Nordic Rooms',
      type: 'Lounge chair',
      material: 'Full-grain leather · Walnut frame',
      dimensions: {
        w: 78,
        d: 82,
        h: 96,
      },
      price: 1780,
      leadTime: 'Made to order · 4 weeks',
      inStock: false,
      assembly: 'Arrives fully assembled',
      delivery: 'Delivery AED 150',
      care: 'Condition the leather twice a year',
      finishes: [
        {
          id: 'cognac',
          label: 'Cognac leather',
          hex: '#9A5B33',
          image: ARMCHAIR,
        },
        {
          id: 'black',
          label: 'Black leather',
          hex: '#2A2A2C',
          image: ARMCHAIR_2,
        },
      ],
    },
  },
  {
    id: 'f-4',
    title: 'Hale Upholstered Bed',
    price: 'AED 3,900',
    location: 'Nordic Rooms · Dubai Design District',
    images: [BED, BED_2],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    furniture: {
      brand: 'Nordic Rooms',
      type: 'King bed frame',
      material: 'Linen-blend upholstery · Slatted base',
      dimensions: {
        w: 200,
        d: 215,
        h: 110,
      },
      price: 3900,
      leadTime: 'In stock · delivered in 7 days',
      inStock: true,
      assembly: 'Assembled in the room, free',
      delivery: 'Free delivery across the UAE',
      care: 'Vacuum upholstery monthly',
      finishes: [
        {
          id: 'oat',
          label: 'Oat linen',
          hex: '#D5C9B2',
          image: BED,
        },
        {
          id: 'charcoal',
          label: 'Charcoal linen',
          hex: '#4A4B50',
          image: BED_2,
        },
      ],
    },
  },
  {
    id: 'f-5',
    title: 'Marlow Writing Desk',
    price: 'AED 1,290',
    location: 'Studio Habitat · Sharjah',
    images: [DESK, SHELF],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    furniture: {
      brand: 'Studio Habitat',
      type: 'Writing desk',
      material: 'Ash veneer · Powder-coated steel',
      dimensions: {
        w: 140,
        d: 60,
        h: 74,
      },
      price: 1290,
      leadTime: 'In stock · delivered in 3 days',
      inStock: true,
      assembly: 'Self-assembly, 20 minutes',
      delivery: 'Free over AED 1,000',
      finishes: [
        {
          id: 'ash',
          label: 'Pale ash',
          hex: '#D7C9B0',
          image: DESK,
        },
      ],
      // This workshop sells through conversation rather than checkout.
      ordering: false,
    },
  },
  {
    id: 'f-6',
    title: 'Pila Modular Shelving',
    price: 'AED 2,150',
    location: 'Studio Habitat · Sharjah',
    images: [SHELF, DESK],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    furniture: {
      brand: 'Studio Habitat',
      type: 'Shelving · 4 modules',
      material: 'Birch ply · Brass fixings',
      dimensions: {
        w: 180,
        d: 35,
        h: 200,
      },
      price: 2150,
      leadTime: 'Made to order · 2 weeks',
      inStock: false,
      assembly: 'Self-assembly, wall fixings included',
      delivery: 'Free over AED 1,000',
      finishes: [
        {
          id: 'birch',
          label: 'Natural birch',
          hex: '#E0CDA9',
          image: SHELF,
        },
        {
          id: 'ink',
          label: 'Ink stain',
          hex: '#2E3440',
          image: DESK,
        },
      ],
      ordering: false,
    },
  },
]

export const furnitureNarrowed = furnitureListings.slice(0, 3)
